import { useEffect, useState } from "react";
import { Avatar, Box, Button, Card, Divider, Typography } from "@mui/material";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import DataSaverOffOutlinedIcon from "@mui/icons-material/DataSaverOffOutlined";
import AssignOrderDialog from "./assign-order";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderDetailsRequest,
  getOrderInspectionRequest,
  getOrderInspectionSuccess,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import {
  downloadInspectionResultsRequest,
  qcRemoveRequest,
  removeAssignedInspectorRequest,
} from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import RemoveInspectorDialog from "./remove-inspector";
import { useNavigate } from "react-router-dom";
import { orderStatus, userType } from "../../../../utils/Constant";
import AssignQCDialog from "./assign-qc";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import Notification from "../../../../components/notification/notification";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

export default function Inspection({ orderId, orderNumber }: any) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showAssign, setShowAssign] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showQCAssign, setShowQCAssign] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { loading, orderInspection, orderDetails } = useSelector(
    (state: any) => state.orders
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    dispatch(getOrderInspectionSuccess({ response: null }));
    dispatch(getOrderInspectionRequest({ orderId }));
  }, []); // eslint-disable-line

  const onRemoveInspector = async () => {
    setShowLoading(true);
    const response = await removeAssignedInspectorRequest(orderId);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderInspectionRequest({ orderId }));
      dispatch(getOrderDetailsRequest({ id: orderId }));
      setShowRemoveDialog(false);
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onRemoveQC = async () => {
    setShowLoading(true);
    const response = await qcRemoveRequest(orderId);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderInspectionRequest({ orderId }));
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onDownloadResultClick = async () => {
    setShowLoading(true);
    try {
      const response = await downloadInspectionResultsRequest([orderId]);

      if (response && response.status === 200) {
        const mimeType =
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([response.data], {
          type: mimeType,
        });

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "downloaded-file";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (
        response &&
        (response.status === 400 || response.status === 404)
      ) {
        setShowErrorNotification(true);
        setErrorMessage(response?.detail);
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <>
      {currentUser?.userType === userType.tenant && (
        <Card className="card-layout">
          <Typography variant="h5">Inspector</Typography>
          <Box className="first-row-layout">
            <Typography className="label-txt">Inspector</Typography>
            {orderInspection?.inspectorFirm ? (
              <Box sx={{ display: "flex", alignItems: "self-start" }}>
                <Box>
                  <Typography fontWeight={550}>Inspection Firm</Typography>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <Avatar
                      alt="Avatar"
                      variant="circular"
                      sx={{ width: 24, height: 24 }}
                      src={orderDetails?.inspectionFirm?.logoUrl}
                    ></Avatar>
                    <Typography color="primary" ml={1.3}>
                      {orderInspection?.inspectorFirm?.inspectorFirmName || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <AttachMoneyOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                    />
                    <Typography ml={1.3}>
                      {orderInspection?.inspectorFirm?.fee
                        ? `$${orderInspection?.inspectorFirm?.fee}`
                        : "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <GradeOutlinedIcon sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
                    <Typography ml={1.3}>
                      {orderInspection?.inspectorFirm?.gradeLetter || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <StraightenOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                    />
                    <Typography ml={1.3}>
                      {orderInspection?.inspectorFirm?.isWithinCoverageArea
                        ? "Within Coverage Area"
                        : "Outside Coverage Area"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <TaskAltOutlinedIcon sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
                    <Typography ml={1.3}>
                      {orderInspection?.inspectorFirm?.completedInspections}{" "}
                      Inspections Completed
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <DataSaverOffOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                    />
                    <Typography ml={1.3}>
                      {orderInspection?.inspectorFirm?.currentInspections}{" "}
                      Assigned / {orderInspection?.inspectorFirm?.maxCapacity}{" "}
                      Max
                    </Typography>
                  </Box>
                </Box>
                <Button
                  startIcon={<ClearOutlinedIcon />}
                  sx={{ ml: 4, mt: -1 }}
                  onClick={() => setShowRemoveDialog(true)}
                  color="error"
                  disabled={
                    orderStatus[
                      orderDetails?.orderStatus as keyof typeof orderStatus
                    ] > 53
                  }
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowAssign(true)}
                disabled={
                  orderStatus[
                    orderDetails?.orderStatus as keyof typeof orderStatus
                  ] < 32
                }
              >
                Assign
              </Button>
            )}
          </Box>
        </Card>
      )}

      {orderStatus[orderDetails?.orderStatus as keyof typeof orderStatus] >
        54 &&
        orderStatus[orderDetails?.orderStatus as keyof typeof orderStatus] !==
          71 &&
        currentUser?.userType === userType.client && (
          <Card className="card-layout">
            <Typography variant="h5">Inspection</Typography>
            <Box className="first-row-layout">
              <Typography className="label-txt">Inspection</Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadOutlinedIcon />}
                onClick={onDownloadResultClick}
              >
                Download Results
              </Button>
            </Box>
          </Card>
        )}

      {currentUser?.userType !== userType.client && (
        <Card className="card-layout">
          <Typography variant="h5">Inspection</Typography>
          <Box className="first-row-layout">
            <Typography className="label-txt">Inspection</Typography>
            <Box display={"grid"}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<OpenInNewOutlinedIcon />}
                onClick={() =>
                  navigate("inspection-form", {
                    state: {
                      orderId,
                      isQCReview: false,
                    },
                  })
                }
              >
                Open Inspection
              </Button>
              {orderStatus[
                orderDetails?.orderStatus as keyof typeof orderStatus
              ] > 54 &&
                orderStatus[
                  orderDetails?.orderStatus as keyof typeof orderStatus
                ] !== 71 &&
                currentUser?.userType === userType.tenant && (
                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={onDownloadResultClick}
                  >
                    Download Results
                  </Button>
                )}
            </Box>
          </Box>
        </Card>
      )}

      {orderStatus?.[orderDetails?.orderStatus as keyof typeof orderStatus] >
        50 &&
        orderStatus?.[orderDetails?.orderStatus as keyof typeof orderStatus] <
          56 &&
        currentUser?.userType === userType.tenant && (
          <Card className="card-layout">
            <Typography variant="h5">QC Review</Typography>
            <Box className="first-row-layout">
              <Typography className="label-txt">QC Reviewer</Typography>
              {orderInspection?.qcDetails?.user ? (
                <Box sx={{ display: "flex", alignItems: "self-start" }}>
                  <Box>
                    <Box sx={{ display: "flex" }}>
                      <Avatar
                        alt="Avatar"
                        variant="circular"
                        sx={{ width: 24, height: 24 }}
                        src={orderDetails?.inspectionFirm?.logoUrl}
                      ></Avatar>
                      <Typography color="primary" ml={1}>
                        {orderInspection?.qcDetails?.user?.name || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mt: 1 }}>
                      <PeopleOutlineOutlinedIcon
                        sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                      />
                      <Typography ml={1}>
                        {orderInspection?.qcDetails?.user?.roles || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mt: 1 }}>
                      <EmailOutlinedIcon sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
                      <Typography ml={1}>
                        {orderInspection?.qcDetails?.user?.emails[0]
                          ?.emailAddress || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", mt: 1 }}>
                      <PhoneOutlinedIcon sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
                      <Typography ml={1}>
                        {orderInspection?.qcDetails?.user?.phones[0]
                          ?.phoneNumber || "-"}
                      </Typography>
                    </Box>
                  </Box>
                  {currentUser?.userType === userType.tenant && (
                    <Button
                      startIcon={<ClearOutlinedIcon />}
                      sx={{ ml: 4, mt: -1 }}
                      onClick={onRemoveQC}
                      color="error"
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowQCAssign(true)}
                >
                  Assign
                </Button>
              )}
            </Box>
            <Divider />
            <Box className="first-row-layout">
              <Typography className="label-txt">2nd QC Required?</Typography>
              <Typography>
                {orderInspection?.qcDetails?.requireSecondQC ? "Yes" : "No"}
              </Typography>
            </Box>
          </Card>
        )}

      {showAssign && (
        <AssignOrderDialog
          orderId={orderId}
          orderNumber={orderNumber}
          showDialog={showAssign}
          closeDialog={() => setShowAssign(false)}
        />
      )}
      {showRemoveDialog && (
        <RemoveInspectorDialog
          showDialog={showRemoveDialog}
          closeDialog={() => setShowRemoveDialog(false)}
          onRemove={onRemoveInspector}
        />
      )}
      {showQCAssign && (
        <AssignQCDialog
          showDialog={showQCAssign}
          closeDialog={() => setShowQCAssign(false)}
          orderNumber={orderNumber}
          orderId={orderId}
          isSecondQC={orderInspection?.qcDetails?.requireSecondQC}
          onQCAssignSuccess={() => {
            setShowSuccessNotification(true);
            setSuccessMessage("QC Assigned Successfully.");
          }}
        />
      )}
      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title="Error"
          message={errorMessage}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </>
  );
}
